// Connects the EXISTING Voice/DAR button to the already-implemented live Radio
// layer as a second gesture. No new WebRTC session, Deepgram connection,
// microphone stream, site-selection system or Radio button is created here:
// this only arms the existing useRadioLive controller while the button is held.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useRadioContext } from "@/hooks/useRadioContext";
import { useRadioLive } from "@/hooks/useRadioLive";
import {
  createVoiceGesture,
  RADIO_HOLD_THRESHOLD_MS,
  type VoiceGesture,
} from "@/lib/radio/radio-gesture";

export function useVoiceRadioHold({ onDarTap }: { onDarTap: () => void }) {
  const { user, currentOrg } = useAuth() as any;
  const { activeSite } = useRadioContext();
  const [armed, setArmed] = useState(false);
  const [holding, setHolding] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const heldRef = useRef(false);
  const attemptedRef = useRef(false);

  const orgId = currentOrg?.id ?? null;
  const siteId = activeSite?.id ?? null;

  // PTT changes microphone publication only; the shell stays subscribed.
  const radio = useRadioLive({
    channelId: null,
    siteId,
  });
  // Always call the LATEST controller: the memoized gesture below would
  // otherwise capture the first-render copy, whose stopRadioTransmission
  // closure still sees phase "idle" and returns without saving the transcript.
  const radioRef = useRef(radio);
  radioRef.current = radio;

  // Transmit as soon as the existing session reports ready, if still held.
  useEffect(() => {
    if (!armed || !heldRef.current) return;
    if (radio.canTransmit && !attemptedRef.current) {
      attemptedRef.current = true;
      void radio.startRadioTransmission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, radio.canTransmit]);

  const disarm = useCallback(() => {
    heldRef.current = false;
    setArmed(false);
    setHolding(false);
  }, []);

  const gesture: VoiceGesture = useMemo(
    () =>
      createVoiceGesture({
        onDarTap,
        onHoldChange: setHolding,
        onRadioStart: () => {
          heldRef.current = true;
          attemptedRef.current = false;
          setLocalError(null);
          if (!user || !orgId) {
            setLocalError("Radio unavailable");
            return;
          }
          if (!siteId) {
            // Never fall back into DAR; say plainly why Radio can't start.
            setArmed(true);
            setLocalError("No Radio site selected — open a site Radio thread in Messages");
            return;
          }
          setArmed(true);
        },
        onRadioStop: () => {
          heldRef.current = false;
          void (async () => {
            try {
              await radioRef.current.stopRadioTransmission();
            } finally {
              disarm();
            }
          })();
        },
      }),
    // Recreated when identity/site changes; the gesture itself is stateless
    // between presses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDarTap, orgId, siteId, user, disarm],
  );

  // Interruption safety: backgrounding, lock, unmount, sign-out, site change.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onHidden = () => {
      if (document.visibilityState === "hidden") gesture.cancel();
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      gesture.cancel();
    };
  }, [gesture]);

  useEffect(() => {
    if (!user) {
      gesture.cancel();
    }
  }, [user, gesture]);

  const radioEngaged = armed;
  const errorMessage = localError ?? radio.state.error?.message ?? null;

  return {
    /** Spread on the existing Voice button; keyboard activation stays DAR. */
    handlers: {
      onPointerDown: () => gesture.pointerDown(),
      onPointerUp: () => gesture.pointerUp(),
      onPointerCancel: () => gesture.cancel(),
      onPointerLeave: () => gesture.cancel(),
    },
    holding,
    radioEngaged,
    transmitting: radio.state.phase === "transmitting",
    phase: radio.state.phase,
    elapsedMs: radio.elapsedMs,
    liveTranscript: radio.live,
    participantCount: radio.state.participants.length,
    transcriptionAvailable: radio.state.transcriptionAvailable,
    siteName: activeSite?.name ?? null,
    errorMessage,
    dismissError: () => {
      setLocalError(null);
      radio.clearError();
      disarm();
    },
    holdThresholdMs: RADIO_HOLD_THRESHOLD_MS,
  };
}
